import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Loader2, Trash2 } from 'lucide-react';
import type { SowSummary } from '../../../services/api';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import {
    useAdminCreditPulseSubscriptionTiers,
    useAdminTeamMembers,
    useCreditMutations,
} from '../../../hooks/useApi';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { DialogFrame, Field, FormErrorAlert } from './creditDialogPrimitives';
import { getMutationErrorMessage } from './creditDialogHelpers';
import {
    formatEffectiveAtLabel,
    getPulseSeatLifecycle,
    pulseSeatLifecycleLabel,
    resolvePulseCreditCycleEndEffectiveAt,
    summarizePulseSeatInventory,
    summarizePulseSeatQuantityChange,
    type PulseSeatInventorySummary,
} from './pulseSeatStatus';
import { formatInclusiveDate } from './dateDisplay';
import { toPositiveIntId } from './positiveIntId';

interface EligibleSeatUser {
    userId: number;
    label: string;
    email: string;
}

function selectedIdsEqual(a: number[], b: number[]): boolean {
    if (a.length !== b.length) return false;
    const sa = [...a].sort((x, y) => x - y);
    const sb = [...b].sort((x, y) => x - y);
    return sa.every((v, i) => v === sb[i]);
}

function normalizedSeatAssignmentUserIds(ids: readonly unknown[]): number[] {
    const seen = new Set<number>();
    const out: number[] = [];
    for (const id of ids) {
        const n = toPositiveIntId(id);
        if (n !== undefined && !seen.has(n)) {
            seen.add(n);
            out.push(n);
        }
    }
    return out.sort((a, b) => a - b);
}

export function PulseSeatAssignmentDialog({
    sow,
    open,
    onClose,
}: {
    sow: SowSummary;
    open: boolean;
    onClose: () => void;
}) {
    const tiersQuery = useAdminCreditPulseSubscriptionTiers({ enabled: open });
    const creditMutations = useCreditMutations();
    const syncMutation = creditMutations.syncPulseSeatAssignments;
    const { hasPermission } = useUserPermissions();
    const canAssign =
        hasPermission('manage:seat_assignments') || hasPermission('administrate:organizations');

    const pulseSub =
        sow.PulseSubscriptions?.find((p) => p.sowId === sow.id) ?? sow.PulseSubscriptions?.[0];

    const sowTeamIdSet = useMemo(
        () => new Set((sow.SowTeams ?? []).map((st) => st.teamId)),
        [sow.SowTeams],
    );

    const sowTeamIdsSorted = useMemo(() => [...sowTeamIdSet].sort((a, b) => a - b), [sowTeamIdSet]);

    const membersQuery = useAdminTeamMembers(
        sowTeamIdsSorted,
        open && Boolean(pulseSub) && sowTeamIdsSorted.length > 0,
    );

    const seatsSignature = useMemo(() => {
        if (!pulseSub?.PulseSeats?.length) {
            return '';
        }
        return pulseSub.PulseSeats.filter((s) => s.userId != null && s.userId !== undefined)
            .map((s) => String(s.userId))
            .sort()
            .join(',');
    }, [pulseSub?.PulseSeats]);

    const serverAssignedIds = useMemo(() => {
        const raw = pulseSub?.PulseSeats?.map((s) => s.userId) ?? [];
        return normalizedSeatAssignmentUserIds(raw);
    }, [pulseSub?.PulseSeats]);

    const [draftUserIds, setDraftUserIds] = useState<number[]>(() => serverAssignedIds);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        setDraftUserIds(serverAssignedIds);
        setSearchQuery('');
    }, [pulseSub?.id, seatsSignature, serverAssignedIds]);

    const eligibleById = useMemo(() => {
        const map = new Map<number, EligibleSeatUser>();
        for (const u of membersQuery.data?.users ?? []) {
            const userId = toPositiveIntId(u.userId);
            if (userId === undefined) {
                continue;
            }
            const nameParts = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();
            const label = nameParts.length > 0 ? nameParts : u.email;
            map.set(userId, { userId, label, email: u.email });
        }
        return map;
    }, [membersQuery.data?.users]);

    const tierName =
        tiersQuery.data?.tiers.find((tier) => tier.id === pulseSub?.pulseSubscriptionTierId)
            ?.name ?? 'Pulse';

    const queryLower = searchQuery.trim().toLowerCase();
    const searchMatches = useMemo(() => {
        const all = [...eligibleById.values()];
        const filtered = queryLower
            ? all.filter((u) => {
                  return (
                      u.email.toLowerCase().includes(queryLower) ||
                      u.label.toLowerCase().includes(queryLower)
                  );
              })
            : all;

        filtered.sort((a, b) => a.label.localeCompare(b.label));

        return filtered.filter((u) => !draftUserIds.includes(u.userId)).slice(0, 12);
    }, [eligibleById, queryLower, draftUserIds]);

    const seatInventory = useMemo(
        () => summarizePulseSeatInventory(pulseSub?.PulseSeats ?? []),
        [pulseSub?.PulseSeats],
    );

    const assignedSeatRows = useMemo(() => {
        const seats = pulseSub?.PulseSeats ?? [];
        return [...draftUserIds]
            .map((userId) => {
                const profile = eligibleById.get(userId);
                const seat = seats.find(
                    (s) => toPositiveIntId(s.userId) === userId && s.inUse === true,
                );
                let assignedLabel = '—';
                if (seat?.assignedAt) {
                    const d = new Date(seat.assignedAt);
                    assignedLabel = Number.isNaN(d.getTime())
                        ? String(seat.assignedAt)
                        : d.toLocaleString();
                }
                const lifecycle = seat ? getPulseSeatLifecycle(seat) : null;
                return {
                    userId,
                    displayName: profile?.label ?? `User ${userId}`,
                    email: profile?.email ?? '—',
                    assignedLabel,
                    lifecycle,
                    lifecycleDetail:
                        seat && lifecycle === 'pending_downgrade' && seat.activeToDate
                            ? `Expires ${formatInclusiveDate(seat.activeToDate)}`
                            : null,
                };
            })
            .sort((a, b) =>
                a.displayName.localeCompare(b.displayName, undefined, {
                    sensitivity: 'base',
                }),
            );
    }, [pulseSub?.PulseSeats, draftUserIds, eligibleById]);

    if (!pulseSub) {
        return null;
    }

    const seatsAllowed = seatInventory.currentPurchasedCount;
    const seatsUsed = draftUserIds.length;
    const dirty = !selectedIdsEqual(draftUserIds, serverAssignedIds);
    const loadingMembers = membersQuery.isLoading || membersQuery.isFetching;
    const atCapacity = seatsUsed >= seatsAllowed;

    async function persistAssignments(nextIds: readonly unknown[]) {
        if (!canAssign || !pulseSub) {
            return;
        }

        await syncMutation.mutateAsync({
            subscriptionId: pulseSub.id,
            userIds: normalizedSeatAssignmentUserIds(nextIds),
            sowId: sow.id,
        });
    }

    return (
        <DialogFrame
            open={open}
            title={`${tierName} seat assignments`}
            description={`${seatsUsed} of ${seatsAllowed} seats assigned for this SOW.${seatInventory.pendingDowngradeCount > 0 ? ` ${seatInventory.pendingDowngradeCount} pending downgrade until cycle end.` : ''}`}
            onClose={onClose}
            panelClassName="max-w-5xl"
        >
            <section className="space-y-4">
                <Card>
                    <CardContent className="space-y-4 p-6">
                        <div className="flex flex-wrap items-baseline gap-2">
                            <span className="text-sm font-medium text-neutral-800">
                                {seatsUsed} / {seatsAllowed} seats assigned
                            </span>
                            {loadingMembers ? (
                                <span className="inline-flex items-center gap-1 text-xs text-neutral-500">
                                    <Loader2 className="size-3.5 animate-spin" />
                                    Loading team members...
                                </span>
                            ) : null}
                            {!loadingMembers && sowTeamIdSet.size === 0 ? (
                                <p className="text-sm text-amber-800">
                                    This SOW has no teams linked yet. Add teams to the SOW before
                                    assigning seats.
                                </p>
                            ) : null}
                            {!loadingMembers &&
                                sowTeamIdSet.size > 0 &&
                                eligibleById.size === 0 &&
                                !membersQuery.isError && (
                                    <p className="text-sm text-neutral-600">
                                        No active members matched the SOW-linked team IDs via the
                                        admin directory. Invite users onto those teams, then try
                                        again.
                                    </p>
                                )}
                            {membersQuery.isError ? (
                                <p className="text-sm text-red-600">
                                    Unable to load team members. Refresh and try again.
                                </p>
                            ) : null}
                        </div>

                        {canAssign ? (
                            <div className="space-y-2">
                                <div className="flex flex-wrap items-end gap-2">
                                    <div className="min-w-[200px] max-w-md flex-1 space-y-1">
                                        <label
                                            htmlFor="pulse-seat-search"
                                            className="text-xs font-medium text-neutral-600"
                                        >
                                            Search eligible users by name or email
                                        </label>
                                        <Input
                                            id="pulse-seat-search"
                                            type="search"
                                            placeholder={
                                                sowTeamIdSet.size === 0
                                                    ? 'Link teams first'
                                                    : 'Type to filter…'
                                            }
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            disabled={
                                                sowTeamIdSet.size === 0 ||
                                                loadingMembers ||
                                                atCapacity
                                            }
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                {atCapacity && (
                                    <p className="text-sm text-neutral-600">
                                        All purchased seats are assigned. Unassign someone or
                                        increase the seat count on this SOW&apos;s Pulse
                                        subscription before adding more users.
                                    </p>
                                )}
                                {!atCapacity &&
                                    searchMatches.length > 0 &&
                                    sowTeamIdSet.size > 0 &&
                                    queryLower !== '' && (
                                        <div className="overflow-hidden rounded-md border border-neutral-200 bg-neutral-50/80">
                                            <ul className="max-h-48 divide-y divide-neutral-100 overflow-auto text-sm">
                                                {searchMatches.map((candidate) => (
                                                    <li key={candidate.userId}>
                                                        <button
                                                            type="button"
                                                            className="flex w-full items-start gap-3 px-3 py-2.5 text-left hover:bg-neutral-100"
                                                            disabled={draftUserIds.includes(
                                                                candidate.userId,
                                                            )}
                                                            onClick={() => {
                                                                if (
                                                                    draftUserIds.length >=
                                                                    seatsAllowed
                                                                ) {
                                                                    return;
                                                                }
                                                                setDraftUserIds(
                                                                    [
                                                                        ...draftUserIds,
                                                                        candidate.userId,
                                                                    ].sort((a, b) => a - b),
                                                                );
                                                                setSearchQuery('');
                                                            }}
                                                        >
                                                            <span className="flex-1">
                                                                <span className="font-medium text-neutral-900">
                                                                    {candidate.label}
                                                                </span>
                                                                <span className="mt-0.5 block text-xs text-neutral-500">
                                                                    {candidate.email}{' '}
                                                                    <span className="tabular-nums text-neutral-400">
                                                                        (ID {candidate.userId})
                                                                    </span>
                                                                </span>
                                                            </span>
                                                            <span className="text-xs font-medium text-[#276db0]">
                                                                Assign
                                                            </span>
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                {!atCapacity &&
                                    queryLower !== '' &&
                                    sowTeamIdSet.size > 0 &&
                                    eligibleById.size > 0 &&
                                    searchMatches.length === 0 && (
                                        <p className="text-sm text-neutral-500">
                                            No eligible users matched your search within this
                                            SOW&apos;s teams.
                                        </p>
                                    )}
                            </div>
                        ) : (
                            <p className="text-sm text-neutral-600">
                                You can view Pulse seat holders here; assigning requires{' '}
                                <span className="font-mono text-xs">
                                    administrate:organizations
                                </span>{' '}
                                or{' '}
                                <span className="font-mono text-xs">manage:seat_assignments</span>.
                            </p>
                        )}

                        <div className="space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <h3 className="text-sm font-semibold text-neutral-950">
                                    Assigned seats ({draftUserIds.length})
                                </h3>
                                {draftUserIds.length > 0 && seatsAllowed > 0 ? (
                                    <p className="text-xs text-neutral-500">
                                        {dirty ? 'Pending save — discard or save to sync' : 'Saved'}
                                    </p>
                                ) : null}
                            </div>
                            {draftUserIds.length === 0 ? (
                                <p className="text-sm text-neutral-500">Nobody has a seat yet.</p>
                            ) : (
                                <Card className="overflow-hidden rounded-md border border-neutral-200">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-12 text-muted-foreground">
                                                    #
                                                </TableHead>
                                                <TableHead>Name</TableHead>
                                                <TableHead>Email</TableHead>
                                                <TableHead className="w-[96px]">User ID</TableHead>
                                                <TableHead className="hidden w-[168px] sm:table-cell">
                                                    Assigned
                                                </TableHead>
                                                {canAssign ? (
                                                    <TableHead className="w-[112px] text-right">
                                                        Actions
                                                    </TableHead>
                                                ) : null}
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {assignedSeatRows.map((row, index) => (
                                                <TableRow key={row.userId}>
                                                    <TableCell className="text-sm tabular-nums text-neutral-500">
                                                        {index + 1}
                                                    </TableCell>
                                                    <TableCell className="font-medium text-neutral-950">
                                                        {row.displayName}
                                                    </TableCell>
                                                    <TableCell className="max-w-[min(100vw,280px)] truncate text-neutral-700">
                                                        {row.email}
                                                    </TableCell>
                                                    <TableCell className="text-sm tabular-nums text-neutral-600">
                                                        {row.userId}
                                                    </TableCell>
                                                    <TableCell className="hidden text-sm text-neutral-600 sm:table-cell">
                                                        <span className="block">
                                                            {row.assignedLabel}
                                                        </span>
                                                        {row.lifecycle === 'pending_downgrade' ? (
                                                            <span className="mt-0.5 block text-xs font-medium text-amber-800">
                                                                {pulseSeatLifecycleLabel(
                                                                    row.lifecycle,
                                                                )}
                                                                {row.lifecycleDetail
                                                                    ? ` · ${row.lifecycleDetail}`
                                                                    : ''}
                                                            </span>
                                                        ) : null}
                                                    </TableCell>
                                                    {canAssign ? (
                                                        <TableCell className="text-right">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="sm"
                                                                className="gap-1.5 text-red-600 hover:bg-red-50 hover:text-red-700"
                                                                onClick={() =>
                                                                    setDraftUserIds(
                                                                        draftUserIds.filter(
                                                                            (id) =>
                                                                                id !== row.userId,
                                                                        ),
                                                                    )
                                                                }
                                                            >
                                                                <Trash2 className="size-4" />
                                                                <span className="sr-only sm:not-sr-only">
                                                                    Unassign
                                                                </span>
                                                            </Button>
                                                        </TableCell>
                                                    ) : null}
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Card>
                            )}
                        </div>

                        {canAssign ? (
                            <div className="flex flex-wrap gap-2 border-t border-neutral-100 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    disabled={
                                        syncMutation.isPending || !dirty || sowTeamIdSet.size === 0
                                    }
                                    onClick={() => {
                                        syncMutation.reset();
                                        setDraftUserIds(serverAssignedIds);
                                    }}
                                >
                                    Discard changes
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    className="bg-neutral-950 text-white hover:bg-neutral-900"
                                    disabled={
                                        syncMutation.isPending ||
                                        !dirty ||
                                        sowTeamIdSet.size === 0 ||
                                        draftUserIds.length > seatsAllowed
                                    }
                                    onClick={() => void persistAssignments(draftUserIds)}
                                >
                                    {syncMutation.isPending ? (
                                        <span className="inline-flex items-center gap-2">
                                            <Loader2 className="size-4 animate-spin" /> Saving...
                                        </span>
                                    ) : (
                                        'Save assignments'
                                    )}
                                </Button>
                            </div>
                        ) : null}

                        {syncMutation.isError ? (
                            <FormErrorAlert
                                message={getMutationErrorMessage(
                                    syncMutation.error,
                                    'Seat assignment could not be saved.',
                                )}
                            />
                        ) : null}
                    </CardContent>
                </Card>
            </section>
        </DialogFrame>
    );
}

const seatCountStepperButtonClassName =
    'flex flex-1 items-center justify-center px-2 text-neutral-600 transition-colors hover:bg-neutral-200/60 hover:text-neutral-900 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent';

function parseSeatCountValue(value: string, min = 0): number {
    const parsed = Number(value);
    if (Number.isInteger(parsed) && Number.isFinite(parsed)) {
        return Math.max(min, parsed);
    }
    return min;
}

interface SeatCountStepperProps {
    value: string;
    onChange: (value: string) => void;
    min?: number;
    'aria-label': string;
}

function SeatCountStepper({
    value,
    onChange,
    min = 0,
    'aria-label': ariaLabel,
}: SeatCountStepperProps) {
    const currentValue = parseSeatCountValue(value, min);

    function adjust(delta: number) {
        onChange(String(Math.max(min, currentValue + delta)));
    }

    return (
        <div className="flex max-w-[10rem] items-stretch overflow-hidden rounded-md admin-field-fill ring-1 ring-inset ring-neutral-200/80">
            <Input
                type="number"
                min={String(min)}
                aria-label={ariaLabel}
                value={value}
                onChange={(event) => onChange(event.target.value)}
                className="min-w-0 flex-1 rounded-none border-0 bg-transparent shadow-none [appearance:textfield] focus-visible:ring-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
            <div className="flex flex-col border-l border-neutral-200/80">
                <button
                    type="button"
                    className={seatCountStepperButtonClassName}
                    aria-label="Increase seat count"
                    onClick={() => adjust(1)}
                >
                    <ChevronUp className="size-3.5" aria-hidden />
                </button>
                <button
                    type="button"
                    className={`${seatCountStepperButtonClassName} border-t border-neutral-200/80`}
                    aria-label="Decrease seat count"
                    disabled={currentValue <= min}
                    onClick={() => adjust(-1)}
                >
                    <ChevronDown className="size-3.5" aria-hidden />
                </button>
            </div>
        </div>
    );
}

interface PulseSeatQuantityDialogProps {
    open: boolean;
    onClose: () => void;
    sow: SowSummary;
    pulseSeatInventory: PulseSeatInventorySummary;
}

export function PulseSeatQuantityDialog({
    open,
    onClose,
    sow,
    pulseSeatInventory,
}: PulseSeatQuantityDialogProps) {
    const tiersQuery = useAdminCreditPulseSubscriptionTiers({ enabled: open });
    const creditMutations = useCreditMutations();
    const changeMutation = creditMutations.changeSowPulseSeatQuantity;
    const { hasPermission } = useUserPermissions();
    const canManageSeats =
        hasPermission('manage:seat_assignments') || hasPermission('administrate:organizations');

    const pulseSub =
        sow.PulseSubscriptions?.find((pulseSubscription) => pulseSubscription.sowId === sow.id) ??
        sow.PulseSubscriptions?.[0];
    const tierName =
        tiersQuery.data?.tiers.find((tier) => tier.id === pulseSub?.pulseSubscriptionTierId)
            ?.name ?? 'Pulse';
    const [targetSeatCount, setTargetSeatCount] = useState('');
    const [useCustomExpiration, setUseCustomExpiration] = useState(false);
    const [customEffectiveAt, setCustomEffectiveAt] = useState('');
    const [reason, setReason] = useState('');
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open || !pulseSub) {
            return;
        }
        const defaultEffectiveAt = resolvePulseCreditCycleEndEffectiveAt(sow);
        const defaultSeatCount = defaultEffectiveAt
            ? summarizePulseSeatQuantityChange(pulseSub.PulseSeats ?? [], 0, defaultEffectiveAt)
                  .pendingSeatCount
            : pulseSeatInventory.futurePurchasedCount;
        setTargetSeatCount(String(defaultSeatCount));
        setUseCustomExpiration(false);
        setCustomEffectiveAt('');
        setReason('');
        setError(null);
    }, [open, pulseSub, sow, pulseSeatInventory.futurePurchasedCount]);

    const liveSeatInventory = useMemo(
        () => summarizePulseSeatInventory(pulseSub?.PulseSeats ?? []),
        [pulseSub?.PulseSeats],
    );
    const openSeatRows = useMemo(() => {
        return (pulseSub?.PulseSeats ?? [])
            .filter(
                (seat) => seat.inUse !== true || seat.userId === null || seat.userId === undefined,
            )
            .map((seat) => {
                const lifecycle = getPulseSeatLifecycle(seat);
                return {
                    seatId: seat.id,
                    lifecycle,
                    lifecycleDetail:
                        lifecycle === 'pending_downgrade' && seat.activeToDate
                            ? `Expires ${formatInclusiveDate(seat.activeToDate)}`
                            : null,
                };
            });
    }, [pulseSub?.PulseSeats]);
    const cycleEndEffectiveAt = useMemo(() => resolvePulseCreditCycleEndEffectiveAt(sow), [sow]);
    const parsedTargetSeatCount = Number(targetSeatCount);
    const isValidSeatCount =
        Number.isInteger(parsedTargetSeatCount) &&
        Number.isFinite(parsedTargetSeatCount) &&
        parsedTargetSeatCount >= 0;
    const resolvedEffectiveAt = useCustomExpiration
        ? customEffectiveAt.trim()
        : (cycleEndEffectiveAt ?? '');
    const seatChangeSummary = useMemo(() => {
        if (!pulseSub || !isValidSeatCount || resolvedEffectiveAt === '') {
            return null;
        }
        return summarizePulseSeatQuantityChange(
            pulseSub.PulseSeats ?? [],
            parsedTargetSeatCount,
            resolvedEffectiveAt,
        );
    }, [isValidSeatCount, parsedTargetSeatCount, pulseSub, resolvedEffectiveAt]);

    if (!pulseSub) {
        return null;
    }

    const currentSeatCount = liveSeatInventory.currentPurchasedCount;
    const autoExpiredSeatCount = seatChangeSummary?.autoExpiredSeatCount ?? 0;
    const occupiedSeatCountToExpire = seatChangeSummary?.occupiedSeatCountToExpire ?? 0;
    const effectiveAtLabel = formatEffectiveAtLabel(
        useCustomExpiration ? customEffectiveAt : cycleEndEffectiveAt,
    );
    const hasEffectiveAt = resolvedEffectiveAt !== '';
    const reasonProvided = reason.trim() !== '';
    const canSubmitChange = canManageSeats && isValidSeatCount && hasEffectiveAt && reasonProvided;

    async function persistChange() {
        if (!canSubmitChange) {
            return;
        }
        setError(null);
        try {
            await changeMutation.mutateAsync({
                sowId: sow.id,
                request: {
                    targetSeatCount: parsedTargetSeatCount,
                    effectiveAt: resolvedEffectiveAt,
                    reason: reason.trim(),
                },
            });
            onClose();
        } catch (err) {
            setError(getMutationErrorMessage(err, 'Could not change seat quantity.'));
        }
    }

    return (
        <DialogFrame
            open={open}
            title={`${tierName} seat quantity`}
            description="Adjust purchased seats for this SOW. Reductions expire open seats first, then occupied seats if needed."
            onClose={onClose}
            panelClassName="max-w-5xl"
        >
            <section className="space-y-4">
                {error ? <FormErrorAlert message={error} /> : null}
                <Card>
                    <CardContent className="space-y-4 p-6">
                        <Field label="Seat count" required>
                            <SeatCountStepper
                                aria-label="Seat count"
                                value={targetSeatCount}
                                onChange={setTargetSeatCount}
                            />
                        </Field>
                        <Field
                            label="Reason for change"
                            required
                            hint="For auditing: saved with this action so the history can be reviewed later."
                        >
                            <Input
                                aria-label="Reason for change"
                                value={reason}
                                onChange={(event) => setReason(event.target.value)}
                            />
                        </Field>
                        <div className="space-y-3 rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
                            <p>
                                Removed seats expire at{' '}
                                {useCustomExpiration
                                    ? customEffectiveAt.trim()
                                        ? effectiveAtLabel
                                        : 'the custom date you select below'
                                    : effectiveAtLabel}
                                .
                            </p>
                            <label className="flex cursor-pointer items-start gap-2">
                                <input
                                    type="checkbox"
                                    className="mt-0.5 size-4 rounded border-neutral-300"
                                    checked={useCustomExpiration}
                                    onChange={(event) => {
                                        setUseCustomExpiration(event.target.checked);
                                        if (!event.target.checked) {
                                            setCustomEffectiveAt('');
                                        }
                                    }}
                                />
                                <span>Custom expiration date</span>
                            </label>
                            {useCustomExpiration ? (
                                <Field label="Expiration date" required>
                                    <Input
                                        type="date"
                                        aria-label="Expiration date"
                                        value={customEffectiveAt}
                                        onChange={(event) =>
                                            setCustomEffectiveAt(event.target.value)
                                        }
                                    />
                                </Field>
                            ) : null}
                            {!useCustomExpiration && !cycleEndEffectiveAt ? (
                                <p className="text-amber-800">
                                    No current billing cycle end was found for this SOW. Enable
                                    custom expiration date to schedule seat removals.
                                </p>
                            ) : null}
                        </div>
                        <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-sm text-neutral-700">
                            <p>Current purchased seats: {currentSeatCount}</p>
                            {seatChangeSummary ? (
                                <p>
                                    Seats still active past {effectiveAtLabel}:{' '}
                                    {seatChangeSummary.pendingSeatCount}
                                </p>
                            ) : null}
                            <p>Currently assigned seats: {liveSeatInventory.assignedCount}</p>
                            {liveSeatInventory.pendingDowngradeCount > 0 ? (
                                <p className="text-amber-800">
                                    {liveSeatInventory.pendingDowngradeCount} seat
                                    {liveSeatInventory.pendingDowngradeCount === 1 ? '' : 's'}{' '}
                                    already pending downgrade
                                    {liveSeatInventory.pendingDowngradeExpirationLabels.length > 0
                                        ? ` (${liveSeatInventory.pendingDowngradeExpirationLabels.join(', ')})`
                                        : ''}
                                    .
                                </p>
                            ) : null}
                            <p>Open seats that will expire automatically: {autoExpiredSeatCount}</p>
                            <p>
                                Occupied seats that may be scheduled to expire:{' '}
                                {occupiedSeatCountToExpire}
                            </p>
                        </div>
                        {openSeatRows.some((seat) => seat.lifecycle === 'pending_downgrade') ? (
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-neutral-800">
                                    Open seats already scheduled to expire
                                </p>
                                <ul className="space-y-2 text-sm text-neutral-700">
                                    {openSeatRows
                                        .filter((seat) => seat.lifecycle === 'pending_downgrade')
                                        .map((seat) => (
                                            <li
                                                key={seat.seatId}
                                                className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
                                            >
                                                <span className="font-medium text-amber-900">
                                                    Pending downgrade
                                                </span>
                                                {seat.lifecycleDetail ? (
                                                    <span className="mt-0.5 block text-amber-800">
                                                        {seat.lifecycleDetail}
                                                    </span>
                                                ) : null}
                                            </li>
                                        ))}
                                </ul>
                            </div>
                        ) : null}
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button type="button" variant="outline" onClick={onClose}>
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                disabled={!canSubmitChange || changeMutation.isPending}
                                onClick={() => void persistChange()}
                            >
                                {changeMutation.isPending ? 'Saving...' : 'Change quantity'}
                            </Button>
                        </div>
                        {changeMutation.isError ? (
                            <p className="text-sm text-red-600">
                                {(changeMutation.error as Error)?.message ??
                                    'Seat quantity could not be updated.'}
                            </p>
                        ) : null}
                    </CardContent>
                </Card>
            </section>
        </DialogFrame>
    );
}
